import { Box } from '@mui/material';
import { Variants } from 'common';
import { BookingWizardFlowTypes } from 'common/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import React, { FC, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import useCaseChooseSessionLength from 'application/modules/bookingWizard/useCases/hooks/useCaseChooseSessionLength';
import {
  GetSimulatorLocationsAdapter,
  IGetSimulatorLocationsHook,
} from 'application/modules/bookingWizard/useCases/hooks/useCaseGetSimulatorLocations';
import useCaseSelectExperience from 'application/modules/bookingWizard/useCases/hooks/useCaseSelectExperience';
import { WizardStep } from 'domain/entities/WizardStep';
import { useHookInjection } from 'domain/hooks';
import BookingCard from 'infrastructure/components/BookingCard';
import { StyledBookingCardContentSpacer } from 'infrastructure/components/BookingCard/style';
import NavigationBar from 'infrastructure/components/NavigationBar';
import PageLayout from 'infrastructure/components/PageLayout';
import {
  selectLocation,
  selectLocationDetails,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import { setActiveInstructorTab, setExperienceType } from 'infrastructure/redux/slices/bookingWizard.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import { TextCopyContext } from 'infrastructure/targets/web/contexts/TextCopyContext';
import LocationSelect from 'infrastructure/targets/web/modules/bookingWizard/components/LocationSelect';
import useExternalNavigation from 'infrastructure/targets/web/modules/bookingWizard/hooks/useExternalNavigation';
import {
  formatToCurrency,
  isAustralia,
  isUS,
  resolveLocationDuckpinUrl,
} from 'infrastructure/targets/web/modules/common/helpers';
import useEventTracking, {
  EventType,
} from 'infrastructure/targets/web/modules/common/hooks/useEventTracking';
import {
  getBookingImage,
  getBowlingBookingImage,
  getClubFittingImage,
  getLessonBookingImage,
} from 'infrastructure/targets/web/modules/common/imageUrlHelpers';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';

import { StyledCardsContainer } from './style';

interface ISelectExperienceStep {
  stepName: WizardStep;
}
const SelectExperienceStep: FC<ISelectExperienceStep> = ({ stepName }) => {
  const { getTextCopy } = useContext(TextCopyContext);
  const { prevStep, nextStep, preselectLocation, redirectToSelectedExperience } =
    useCaseSelectExperience(stepName);
  const { preselectPartySize, preselectReservationDate } = useCaseChooseSessionLength();
  const { openPageInANewTab } = useExternalNavigation();
  const dispatch = useAppDispatch();
  const selectedLocationId = useAppSelector(selectLocation);
  const selectedLocationDetails = useAppSelector(selectLocationDetails);
  const { sendEvent } = useEventTracking();
  const isGolfVariant = [process.env.VITE_VARIANT].includes(Variants.FIVEIRON);
  const [duckpinWebsiteUrl, setDuckpinWebsiteUrl] = React.useState<string | undefined>(undefined);

  const t = useTranslationPrefix('book.select_fitting_type');

  const { search } = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const getLocationsHook = useHookInjection<IGetSimulatorLocationsHook>(
    GetSimulatorLocationsAdapter,
  );
  const { inProgress: areLocationsFetching } = getLocationsHook;
  const experienceSlug = query?.get('experience');
  const locationSlug = query?.get('location');
  const partySizeSlug = query?.get('partySize');
  const startDateSlug = query?.get('startDate');

  useEffect(() => {
    setDuckpinWebsiteUrl(resolveLocationDuckpinUrl(selectedLocationDetails));
  }, [selectedLocationDetails]);

  useEffect(() => {
    if (!areLocationsFetching) {
      if (locationSlug) {
        preselectLocation(locationSlug);
      }
      if (partySizeSlug) {
        preselectPartySize(partySizeSlug);
      }
      if (startDateSlug) {
        preselectReservationDate(startDateSlug);
      }
      if (experienceSlug) {
        redirectToSelectedExperience(experienceSlug, partySizeSlug, startDateSlug);
      }
    }
  }, [experienceSlug, locationSlug, partySizeSlug, startDateSlug, areLocationsFetching]);

  const handleCardClick = (experienceType: string) => {
    dispatch(setExperienceType({ experienceType }));
    dispatch(setActiveInstructorTab({ activeInstructorTab: experienceType }));
    nextStep(experienceType);
    sendEvent(EventType.SelectExperience, {
      experience: experienceType,
      utm: localStorage.getItem('utm') ? JSON.parse(localStorage.getItem('utm') as string) : null,
    });
  };

  const handleDuckpinCardClick = () => {
    if (!duckpinWebsiteUrl) {
      return;
    }
    openPageInANewTab(duckpinWebsiteUrl);
  };

  return (
    <PageLayout pageTitle={'Book Your Next Visit'} shouldFadeIn>
      <NavigationBar backButtonCallback={prevStep}>
        <LocationSelect selectedSimLocationId={selectedLocationId} />
      </NavigationBar>
      <StyledCardsContainer>
        <BookingCard
          style={{ order: 1 }}
          buttons={[
            {
              label: 'Book Now',
              onClick: () => handleCardClick(isGolfVariant ? 'simulator' : 'bowling'),
            },
          ]}
          title={getTextCopy('reserve')}
          content={
            <Box component="span">
              {!isAustralia() ? (
                <>
                  {getTextCopy('reserveDescription1')}
                  <StyledBookingCardContentSpacer />
                </>
              ) : null}
              {getTextCopy('reserveDescription2')}
              <StyledBookingCardContentSpacer />
              {getTextCopy('reserveDescription3')}
            </Box>
          }
          imageUrl={getBookingImage()}
        />
        {isGolfVariant && (
          <>
            <BookingCard
              style={{ order: 2 }}
              title={getTextCopy('book_lesson')}
              buttons={[
                {
                  label: 'Search by Date',
                  onClick: () => handleCardClick(BookingWizardFlowTypes.LessonByDate),
                  variant: 'outlined',
                },
                {
                  label: 'Search by Coach',
                  onClick: () => handleCardClick(BookingWizardFlowTypes.LessonByCoach),
                },
              ]}
              content={
                <Box component="span">
                  Each lesson features state-of-the-art metrics and video analysis on TrackMan
                  simulators.
                  <StyledBookingCardContentSpacer />
                  Unique mentoring based on your skill level and goals for the game.
                  <StyledBookingCardContentSpacer />
                  Pricing is based on the level of instructor.
                </Box>
              }
              imageUrl={getLessonBookingImage()}
            />
            {isUS && (
              <BookingCard
                style={{ order: 3 }}
                title={getTextCopy('book_club_fitting')}
                content={
                  <Box component="span">
                    Work with a certified Callaway Club Fitting Specialist and test the latest
                    models from Callaway Golf.
                    <StyledBookingCardContentSpacer />
                    Custom club fitting services are {formatToCurrency(99)} per hour.
                    <StyledBookingCardContentSpacer />
                  </Box>
                }
                buttons={[
                  {
                    label: t('search_by_time'),
                    onClick: () => handleCardClick(BookingWizardFlowTypes.ClubFitting),
                    variant: 'outlined',
                  },
                  {
                    label: t('search_by_coach'),
                    onClick: () => handleCardClick(BookingWizardFlowTypes.ClubFittingbyCoach),
                  },
                ]}
                imageUrl={getClubFittingImage()}
              />
            )}
            {duckpinWebsiteUrl && (
              <BookingCard
                style={{ order: 4 }}
                title={getTextCopy('book_lane')}
                buttons={[
                  {
                    label: 'Book Now',
                    onClick: () => handleDuckpinCardClick(),
                  },
                ]}
                content={
                  <Box component="span">
                    Book by the hour for up to 6 bowlers per lane.
                    <StyledBookingCardContentSpacer />
                    Let the good times roll! No bowling shoes required.
                    <StyledBookingCardContentSpacer />
                    Smaller balls and shorter lanes mean quicker games and more excitement! Duckpin
                    bowling is serving up strikes and spares with menu items and drinks available to
                    order right to your lane.
                    <StyledBookingCardContentSpacer />
                  </Box>
                }
                imageUrl={getBowlingBookingImage()}
              />
            )}
          </>
        )}
      </StyledCardsContainer>
    </PageLayout>
  );
};

export default SelectExperienceStep;
